// Frontend mirror of the per-client section lock-state in wizard_state.sections
// (server: netlify/functions/_lib/build-chat/sections.ts). Pure read/derive helpers
// shared by the build chat UI; the server stays the source of truth for writes.

export interface SectionState {
  order: number;
  locked: boolean;
  name?: string;
  /** Latest build row id for this section — drives status / preview / lock. */
  buildId?: string;
}
export type Sections = Record<string, SectionState>;

// ── Multi-page layer (multi-page.md §3, M-P2) — frontend read helpers ─────────
// A Site is a tree of Pages, each with its own sections. The rail scopes to the active
// page. Back-compat: a legacy client with only a flat `sections` key reads as a one-page
// tree (Home). The server (build-chat-pages / withSections) owns writes + migration.
export const HOME_PAGE_ID = 'home';

export interface PageState {
  slug: string;
  name: string;
  order: number;
  inNav: boolean;
  navLabel?: string;
  isHome?: boolean;
  sections: Sections;
  seo?: Record<string, unknown>;
}
export type Pages = Record<string, PageState>;

function parseSections(raw: unknown): Sections {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const out: Sections = {};
  for (const [id, v] of Object.entries(raw as Record<string, unknown>)) {
    if (v && typeof v === 'object') {
      const s = v as Partial<SectionState>;
      out[id] = {
        order: typeof s.order === 'number' ? s.order : 0,
        locked: s.locked === true,
        name: typeof s.name === 'string' ? s.name : undefined,
        buildId: typeof s.buildId === 'string' ? s.buildId : undefined,
      };
    }
  }
  return out;
}

/** Read the pages layer (empty for a legacy/empty client). */
export function readPages(wizardState: Record<string, unknown> | null | undefined): Pages {
  const raw = (wizardState ?? {})['pages'];
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const out: Pages = {};
  for (const [id, v] of Object.entries(raw as Record<string, unknown>)) {
    if (v && typeof v === 'object' && !Array.isArray(v)) {
      const p = v as Record<string, unknown>;
      out[id] = {
        slug: typeof p.slug === 'string' ? p.slug : id,
        name: typeof p.name === 'string' ? p.name : id,
        order: typeof p.order === 'number' ? p.order : 0,
        inNav: p.inNav === true,
        ...(typeof p.navLabel === 'string' ? { navLabel: p.navLabel } : {}),
        ...(p.isHome === true ? { isHome: true } : {}),
        sections: parseSections(p.sections),
        ...(p.seo && typeof p.seo === 'object' && !Array.isArray(p.seo) ? { seo: p.seo as Record<string, unknown> } : {}),
      };
    }
  }
  return out;
}

/** Pages in display/nav order (order then id); Home sorts first (order 0). */
export function orderedPages(pages: Pages): Array<{ id: string } & PageState> {
  return Object.entries(pages)
    .map(([id, p]) => ({ id, ...p }))
    .sort((a, b) => a.order - b.order || a.id.localeCompare(b.id));
}

/** The active page id: activePageId when valid, else Home, else first by order, else 'home'. */
export function activePageId(wizardState: Record<string, unknown> | null | undefined): string {
  const pages = readPages(wizardState);
  const ids = Object.keys(pages);
  if (ids.length === 0) return HOME_PAGE_ID;
  const active = (wizardState ?? {})['activePageId'];
  if (typeof active === 'string' && pages[active]) return active;
  const home = ids.find((id) => pages[id].isHome);
  if (home) return home;
  return orderedPages(pages)[0].id;
}

/**
 * Read a page's sections. Page-scoped (M-P2): the given page, or the active/Home page by
 * default. A legacy client with only a flat `sections` key reads that (back-compat).
 */
export function readSections(
  wizardState: Record<string, unknown> | null | undefined,
  pageId?: string,
): Sections {
  const pages = readPages(wizardState);
  if (Object.keys(pages).length > 0) {
    const id = pageId ?? activePageId(wizardState);
    return pages[id]?.sections ?? {};
  }
  return parseSections((wizardState ?? {})['sections']);
}

export function orderedSections(s: Sections): Array<{ id: string } & SectionState> {
  return Object.entries(s)
    .map(([id, v]) => ({ id, ...v }))
    .sort((a, b) => a.order - b.order || a.id.localeCompare(b.id));
}

export function allLocked(s: Sections): boolean {
  const ids = Object.keys(s);
  return ids.length > 0 && ids.every((id) => s[id].locked);
}

/**
 * The whole-SITE Build gate (multi-page.md §2): Home has ≥1 locked section, and every
 * page that HAS sections has them all locked. A planned page with no sections yet (built
 * from M-P3) does NOT block. For a single-page client this equals allLocked(Home).
 */
export function siteAllLocked(pages: Pages): boolean {
  const home = pages[HOME_PAGE_ID] ?? Object.values(pages).find((p) => p.isHome);
  if (!home || !allLocked(home.sections)) return false;
  return Object.values(pages).every((p) => {
    const ids = Object.keys(p.sections);
    return ids.length === 0 || ids.every((id) => p.sections[id].locked);
  });
}

/** A page's coarse state for its chip: 'empty' | 'draft' | 'locked' (all sections locked). */
export type PageStatus = 'empty' | 'draft' | 'locked';
export function pageStatus(p: PageState): PageStatus {
  const ids = Object.keys(p.sections);
  if (ids.length === 0) return 'empty';
  return ids.every((id) => p.sections[id].locked) ? 'locked' : 'draft';
}

/**
 * Whole-site Build completion from a wizard_state (the wizard's Review gate). Uses the
 * pages layer when present (siteAllLocked), else the legacy flat sections (a one-page
 * Home) — so single-page clients gate exactly as before.
 */
export function reviewComplete(wizardState: Record<string, unknown> | null | undefined): boolean {
  const pages = readPages(wizardState);
  if (Object.keys(pages).length > 0) return siteAllLocked(pages);
  return allLocked(readSections(wizardState));
}

/**
 * IA-4 (multi-page.md §14): the site-level fan-out target set — every BUILT page (≥1
 * section) paired with its ANCHOR section (lowest-order existing section). A "Whole site"
 * send issues one normal per-(page, anchor) build turn per entry, so `canBuild` is naturally
 * true and the engine/turn path is byte-unchanged. Unbuilt pages (no sections) are skipped —
 * they'll carry the change when first built. A legacy/flat client (no pages layer) → a single
 * synthetic Home entry over its flat sections. Pure.
 */
export interface PageAnchor {
  pageId: string;
  pageName: string;
  anchorId: string;
  anchorName: string;
}
export function builtPageAnchors(wizardState: Record<string, unknown> | null | undefined): PageAnchor[] {
  const pages = readPages(wizardState);
  const layer =
    Object.keys(pages).length > 0
      ? orderedPages(pages).map((p) => ({ id: p.id, name: p.name, sections: p.sections }))
      : [{ id: HOME_PAGE_ID, name: 'Home', sections: readSections(wizardState, HOME_PAGE_ID) }];
  const out: PageAnchor[] = [];
  for (const p of layer) {
    const secs = orderedSections(p.sections);
    if (secs.length === 0) continue; // unbuilt page → nothing to anchor
    out.push({ pageId: p.id, pageName: p.name, anchorId: secs[0].id, anchorName: secs[0].name ?? secs[0].id });
  }
  return out;
}

export function slugifySection(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 80) || 'section';
}

/** #GR24: the section picker's sentinel for "start a new section" (reveals the name box). */
export const NEW_SECTION = '__new__';

/**
 * #GR24: resolve the section picker into a build target. '' → none (just chat); the NEW
 * sentinel → the typed name (create a section); an existing section id → iterate that
 * one. Returns the sectionId (undefined = no section) + a human label for the turn. Pure.
 */
export function resolveSectionChoice(
  choice: string,
  newName: string,
  sections: Sections,
): { sectionId?: string; label: string } {
  if (choice === NEW_SECTION) {
    const label = newName.trim();
    return { sectionId: label ? slugifySection(label) : undefined, label };
  }
  if (choice) return { sectionId: choice, label: sections[choice]?.name ?? choice };
  return { sectionId: undefined, label: '' };
}

/**
 * #GR26A (sticky artboards): which artboard folder a chat upload is filed under. The
 * typed section name wins; else the section the designer is actively working (so a drop
 * made with the name field momentarily blank STILL lands in the section folder and
 * becomes sticky — the build turn's listArtboardPaths finds it on every later turn,
 * instead of rotting in the generic bucket after one turn); else the generic bucket
 * ('seo' for SEO, 'chat' for build) — today's behaviour when no section is in play.
 */
export function resolveUploadSection(
  typedSectionId: string | undefined,
  activeSectionId: string | null,
  isSeo: boolean,
): string {
  return typedSectionId ?? activeSectionId ?? (isSeo ? 'seo' : 'chat');
}
